import type {
  WallPostResponseDTO,
} from '../../../domain/social/wall-contracts.js';
import type { WallProfileResponse } from '../api/WallClient.js';

export type OutboxActionType = 'CREATE_POST' | 'TOGGLE_LIKE' | 'FOLLOW' | 'UNFOLLOW';

export interface OutboxItem<T = any> {
  id: string;
  type: OutboxActionType;
  targetDid: string;
  payload: T;
  createdAt: number;
  status: 'PENDING_MESH_PEER' | 'IN_FLIGHT' | 'FAILED';
  retryCount: number;
}

export interface LocalWallStore {
  getCachedProfile(userDid: string): Promise<WallProfileResponse | null>;
  setCachedProfile(userDid: string, data: WallProfileResponse): Promise<void>;
  getCachedPosts(userDid?: string): Promise<WallPostResponseDTO[]>;
  setCachedPosts(userDid: string, posts: WallPostResponseDTO[]): Promise<void>;
  enqueueOutbox<T>(action: { type: OutboxActionType; targetDid: string; payload: T }): Promise<OutboxItem<T>>;
  getOutbox(): Promise<OutboxItem[]>;
  removeOutboxItem(id: string): Promise<void>;
}

export class MemoryLocalWallStore implements LocalWallStore {
  private profiles = new Map<string, WallProfileResponse>();
  private posts = new Map<string, WallPostResponseDTO[]>();
  private outbox: OutboxItem[] = [];

  async getCachedProfile(userDid: string): Promise<WallProfileResponse | null> {
    return this.profiles.get(userDid) || null;
  }

  async setCachedProfile(userDid: string, data: WallProfileResponse): Promise<void> {
    this.profiles.set(userDid, data);
  }

  async getCachedPosts(userDid?: string): Promise<WallPostResponseDTO[]> {
    if (userDid) {
      return this.posts.get(userDid) || [];
    }
    const all = Array.from(this.posts.values()).flat();
    return all.sort((a, b) => b.createdAt - a.createdAt);
  }

  async setCachedPosts(userDid: string, posts: WallPostResponseDTO[]): Promise<void> {
    this.posts.set(userDid, posts);
  }

  async enqueueOutbox<T>(action: { type: OutboxActionType; targetDid: string; payload: T }): Promise<OutboxItem<T>> {
    const item: OutboxItem<T> = {
      id: `bundle_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      type: action.type,
      targetDid: action.targetDid,
      payload: action.payload,
      createdAt: Date.now(),
      status: 'PENDING_MESH_PEER',
      retryCount: 0,
    };
    this.outbox.push(item);
    return item;
  }

  async getOutbox(): Promise<OutboxItem[]> {
    return [...this.outbox];
  }

  async removeOutboxItem(id: string): Promise<void> {
    this.outbox = this.outbox.filter((item) => item.id !== id);
  }
}

export const localWallStore = new MemoryLocalWallStore();
